import { fetcher, MatsuriError, useAuthFetch } from "matsuri-hooks"
import { AlertMessageType } from "matsuri-ui"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import {
    AssociateListingToOta,
    OrdinanceWeekdayBlockSetting,
    RoomTypeDetail,
    SearchCalendarOperationLogResponse
} from "../domain/roomType"
import { ListingOperationValueType } from "../domain/listing"
import { isGreaterAuthorityThan, isGreaterScopeThan } from "../domain/authority"
import { m2mCore } from "../endpoints/m2m-core"
import { useAuthUser } from "./useAuthUser"

export const useRoomTypeDetail = (token: string, roomTypeId: string | null) => {
    return useAuthFetch<RoomTypeDetail>(
        token,
        roomTypeId ? m2mCore.v1.getRoomType({ id: roomTypeId }) : null,
        {}
    )
}

export const requestGetRoomTypeDetail = (token: string, roomTypeId: string) => {
    return fetcher<RoomTypeDetail>(m2mCore.v1.getRoomType({ id: roomTypeId }), {
        token
    })
}

export const useSearchCalendarOperationLogByRoomTypeId = (
    token: string,
    roomTypeId: string | null,
    input: {
        sinceCreatedAt: number
        limit: number
    }
) => {
    return useAuthFetch<SearchCalendarOperationLogResponse>(
        token,
        roomTypeId
            ? m2mCore.v1.searchCalendarOperationLogByRoomTypeId({
                  roomTypeId
              })
            : null,
        {
            method: "POST",
            body: JSON.stringify(input)
        }
    )
}

export const requestUpdateRoomType = (
    token: string,
    roomTypeId: string,
    input: {
        name?: string
        operationType?: string
    }
) => {
    const endpoint = m2mCore.v1.updateRoomType

    return fetcher(endpoint({ id: roomTypeId }), {
        method: endpoint.method,
        body: JSON.stringify(input),
        token
    })
}

export interface FindOrdinanceWeekdayBlockSettingsResponse {
    data: OrdinanceWeekdayBlockSetting[]
}

export const useOrdinanceWeekdayBlockSettings = (
    token: string,
    roomTypeId: string | null
) => {
    return useAuthFetch<FindOrdinanceWeekdayBlockSettingsResponse>(
        token,
        roomTypeId
            ? m2mCore.v1.findOrdinanceWeekdayBlockSettingsByRoomTypeIds({
                  roomTypeIds: roomTypeId
              })
            : null,
        {}
    )
}

export const requestSaveOrdinanceWeekdayBlockSettings = (
    token: string,
    input: OrdinanceWeekdayBlockSetting
) => {
    const endpoint = m2mCore.v1.saveAllOrdinanceWeekdayBlockSettings

    return fetcher<
        never,
        {
            error: string
            errorType:
                | "ordinance_weekday_block_settings_empty"
                | "ordinance_weekday_block_settings_duplicated_room_type"
                | "ordinance_weekday_block_settings_duplicated_weekday"
                | (string & {})
        }
    >(endpoint(), {
        method: endpoint.method,
        body: JSON.stringify({ data: [input] }),
        token
    })
}

export const getOrdinanceWeekdayBlockSettingsErrorMessage = (
    error: MatsuriError | Error | undefined,
    defaultHappend: string
): AlertMessageType => {
    const errorType =
        error instanceof MatsuriError ? error.parse?.()?.errorType : undefined

    switch (errorType) {
        case "ordinance_weekday_block_settings_duplicated_weekday":
            return {
                happend: defaultHappend,
                reason: "同じ曜日が重複して指定されています。",
                action: "各曜日の設定が1つずつになるように修正してください。"
            }
        case "ordinance_weekday_block_settings_empty":
            return {
                happend: defaultHappend,
                reason: "更新対象のデータが空です。",
                action: "部屋タイプを指定して再度お試しください。"
            }
        case "ordinance_weekday_block_settings_duplicated_room_type":
            return {
                happend: defaultHappend,
                reason: "同じ部屋タイプが重複して指定されています。",
                action: "部屋タイプごとの設定が1つずつになるように修正してください。"
            }
        default:
            return {
                happend: defaultHappend,
                reason: "サーバー内でエラーが発生しました。",
                action: "しばらく時間を置いて再度お試しください。"
            }
    }
}

export const useAllowedAssociateToOTA = (): boolean => {
    const { token } = useAuth()
    const jwt = useAuthUser(token)

    if (!jwt) {
        return false
    }

    return (
        isGreaterAuthorityThan(jwt.authority, "Admin") ||
        (isGreaterAuthorityThan(jwt.authority, "Manager") &&
            isGreaterScopeThan(jwt.scope.listings, "write"))
    )
}

// 今は、useAllowedAssociateToOTAと中身が一緒だが、scopeに関する条件が今後変わるので、別にしている。
export const useAllowedToDetachFromOTA = (): boolean => {
    const { token } = useAuth()
    const jwt = useAuthUser(token)

    if (!jwt) {
        return false
    }

    return (
        isGreaterAuthorityThan(jwt.authority, "Admin") ||
        (isGreaterAuthorityThan(jwt.authority, "Manager") &&
            isGreaterScopeThan(jwt.scope.listings, "admin"))
    )
}

export const requestAssociateToOTA = (
    token: string,
    roomTypeId: string,
    input: AssociateListingToOta
) => {
    const endpoint = m2mCore.v1.associateToOTA

    // status: okしか返ってこない
    return fetcher(endpoint({ id: roomTypeId }), {
        method: endpoint.method,
        body: JSON.stringify(input),
        token
    })
}

export const requestDetachFromOta = (
    token: string,
    input: {
        roomTypeId: string
        otaType: string
        otaId: string
    }
) => {
    const endpoint = m2mCore.v1.detachFromOTA

    return fetcher(
        endpoint({
            id: input.roomTypeId,
            otaType: input.otaType,
            otaId: input.otaId
        }),
        {
            method: endpoint.method,
            token
        }
    )
}

export interface CreateRoomTypeInput {
    name: string
    propertyId: string
    operationType: ListingOperationValueType
    has180Days?: boolean
}

export interface CreateRoomTypeResponse {
    result: "ok"
    roomTypeId: string
}

export const requestCreateRoomType = (
    token: string,
    input: CreateRoomTypeInput
) => {
    const endpoint = m2mCore.v1.createRoomType
    return fetcher<CreateRoomTypeResponse>(
        endpoint({ propertyId: input.propertyId }),
        {
            method: endpoint.method,
            body: JSON.stringify(input),
            token
        }
    )
}
